import {
  AccountContactPreferencesDocument,
  AccountType,
  ClientDetailsDataDocument,
  DerivedLocationSource,
  UpdateAccountHeaderMutation,
  UpdateAccountHeaderMutationVariables,
  useAccountHeaderQuery,
  useAppDataQuery,
  useClientTypesQuery,
  useUpdateAccountHeaderMutation,
} from 'graphql/bindings/AdminBindings'
import {
  BasicAutocompleteField,
  Box,
  FlagField,
  FormModal,
  FormSectionTitle,
  HelperText,
  SpinnerButton,
  TextField,
  Typography,
  ValueAutocompleteField,
} from '@forgeglobal/design-framework'
import { CountryField, InvestorShareholderField } from 'components/shared/dropdowns'
import { DoNotContactFormProps, doNotContactCountries } from 'util/DoNotContactUtils'
import { Field, Form, FormRenderProps } from 'react-final-form'
import { MutationResult } from 'react-apollo'
import { usStateOptions } from 'util/LocationUtils'
import HighlightedSection from 'components/shared/HighlightedSection'
import React, { Fragment, useEffect, useRef } from 'react'
import capitalize from 'lodash/capitalize'
import merge from 'lodash/merge'
import omit from 'lodash/omit'

export interface HeaderFormInputShape {
  id: string
  name?: string
  type: AccountType
  derived_location: {
    source?: DerivedLocationSource | null
  }
  client_type_id?: string
  is_account_of_interest: Boolean
  account_of_interest_reason?: string | null
  account_of_interest_set_by_admin?: {
    id: number
    name: string
  } | null
  is_forge_declined: boolean
  forge_declined_reason?: string | null
  forge_declined_sete_by_admin?: {
    id: number
    name: string
  } | null
  country?: string | null | { label: string; value: string }
  state?: string | null | { label: string; value: string }
  city?: string | null

  investorShareholder: Array<string>
  mutationResult: MutationResult<UpdateAccountHeaderMutation>
}

const HeaderForm: React.FC<FormRenderProps<HeaderFormInputShape> & DoNotContactFormProps> = ({
  handleSubmit,
  form,
  initialValues,
  values,
  valid,
  editSection,
  highlightDNCFields,
}) => {
  const { data, loading } = useClientTypesQuery({ variables: { accountType: values.type } })
  const clientTypes =
    data?.client_types
      ?.map(ctype => ({ label: ctype.name, value: ctype.id }))
      .sort((a, b) => a.label.localeCompare(b.label)) ?? []

  const mutateInProgress = values.mutationResult.called
  const stateOptions =
    (values.state && typeof values.state === 'string' && values.state.length) === 2
      ? usStateOptions.concat({ label: values.state as string, value: values.state as string })
      : usStateOptions
  const { data: appData } = useAppDataQuery()
  const forgeDeclinedRef = useRef(null)
  const locationRef = useRef(null)

  const formChange = form.change // This is hack to get around exhaustive-deps, not binding and it still works [for now]
  useEffect(() => {
    if (values.is_account_of_interest && !initialValues.is_account_of_interest) {
      formChange('account_of_interest_set_by_admin.name', appData?.currentAdmin.name ?? '')
      formChange('account_of_interest_reason', undefined)
    }
    if (values.is_forge_declined && !initialValues.is_forge_declined) {
      formChange('forge_declined_set_by_admin.name', appData?.currentAdmin.name ?? '')
      formChange('forge_declined_reason', undefined)
    }
    if (editSection === 'forge_declined') {
      ;(forgeDeclinedRef.current as any).scrollIntoView()
    } else if (editSection === 'country') {
      ;(locationRef.current as any).scrollIntoView()
    }

    // eslint-disable-next-line
  }, [values.is_account_of_interest, values.is_forge_declined, formChange])
  if (initialValues.is_account_of_interest && !initialValues.account_of_interest_set_by_admin) {
    formChange('account_of_interest_set_by_admin.name', 'System')
  }

  let countryString: string | null = null
  if (values.country) {
    if (typeof values.country === 'string') countryString = values.country
    else countryString = values.country.value as string
  }

  return (
    <form onSubmit={handleSubmit}>
      <FormSectionTitle>Account Details</FormSectionTitle>
      <Field name="name" label="Name" component={TextField} fullWidth />
      <Field name="type" label="Type" format={capitalize} disabled component={TextField} fullWidth />
      <InvestorShareholderField name="investorShareholder" label="Investor/Shareholder" />
      {!loading && <ValueAutocompleteField name="client_type_id" label="Client Type" options={clientTypes} />}

      <HighlightedSection
        highlighted={(highlightDNCFields && doNotContactCountries.includes(countryString)) || false}
        ref={locationRef}>
        <FormSectionTitle>Location</FormSectionTitle>
        <CountryField name="country" label="Country" />
        {countryString === 'US' && <BasicAutocompleteField name="state" label="State" options={stateOptions} />}
        {values.country && <Field name="city" component={TextField} label="City" fullWidth />}
        {values.derived_location.source === DerivedLocationSource.SignupLocation && (
          <HelperText text="This account's location was set automatically based on the user's approximate signup location. Please update as needed." />
        )}
        {values.derived_location.source === DerivedLocationSource.EntityAddress && (
          <HelperText text="This account's location was set automatically based on the address of one of the account's entities. Please update as needed." />
        )}

        {doNotContactCountries.includes(countryString) && (
          <HelperText
            text={`This country's laws prevent Forge from marketing to this account. This account will be added to the "Do Not Contact" list.`}
          />
        )}
      </HighlightedSection>
      <FormSectionTitle>Account of Interest</FormSectionTitle>
      <Field
        id="is_account_of_interest"
        name="is_account_of_interest"
        label="Flag as an Account of Interest?"
        component={FlagField}
        fullWidth
      />
      {values.is_account_of_interest && (
        <Fragment>
          <Field
            name="account_of_interest_set_by_admin.name"
            component={TextField}
            label="Flag set by"
            disabled
            fullWidth
          />
          <Field
            name="account_of_interest_reason"
            label="Reason for AOI"
            component={TextField}
            fullWidth
            multiline
            disabled={
              !values.account_of_interest_set_by_admin || values.account_of_interest_set_by_admin.name === 'System'
            } //disabled for system-set AOI
          />
        </Fragment>
      )}
      <HighlightedSection
        highlighted={(highlightDNCFields && values.is_forge_declined) || false}
        ref={forgeDeclinedRef}>
        <FormSectionTitle>Decline to Contact</FormSectionTitle>
        <Typography variant="caption" color="textSecondary">
          Checking this box will mark the account as Do Not Contact for deals.
        </Typography>
        <Field id="is_forge_declined" name="is_forge_declined" label="Decline" component={FlagField} fullWidth />
        {values.is_forge_declined && (
          <Fragment>
            <Field
              name="forge_declined_set_by_admin.name"
              component={TextField}
              label="Declined by"
              disabled
              fullWidth
            />
            <Field name="forge_declined_reason" label="Reason" component={TextField} fullWidth multiline />
          </Fragment>
        )}
      </HighlightedSection>
      <Box mt={2}>
        <SpinnerButton
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          loading={mutateInProgress}
          caption="Save"
        />
      </Box>
    </form>
  )
}

interface AccountHeaderModalProps {
  hideModal: () => void
  accountId: string
}

export const AccountHeaderModal: React.FC<AccountHeaderModalProps & DoNotContactFormProps> = ({
  hideModal,
  accountId,
  editSection,
  highlightDNCFields,
}) => {
  const { data } = useAccountHeaderQuery({ variables: { id: accountId } })

  const [mutate, mutateResult] = useUpdateAccountHeaderMutation({
    onCompleted: hideModal,
    refetchQueries: [
      { query: ClientDetailsDataDocument, variables: { id: accountId } },
      { query: AccountContactPreferencesDocument, variables: { id: accountId } },
    ],
  })
  const account = data?.account
  const onSubmit = (values: any) => {
    const invShareholderStrings = (values.investorShareholder ?? []).map(
      (e: { label: string; value: string }) => e.value
    )
    const isInvestor: boolean = invShareholderStrings.includes('Investor')
    const isShareholder: boolean = invShareholderStrings.includes('Shareholder')
    const formContents = {
      ...omit(values, [
        'type',
        'investorShareholder',
        'derived_location',
        'mutationResult',
        'account_of_interest_set_by_admin',
        'forge_declined_set_by_admin',
        'country',
        'state',
      ]),
      country: values?.country?.value || values?.country,
      state: values?.state?.value || values?.state,
      is_investor: isInvestor,
      is_shareholder: isShareholder,
      previous_location: omit(account?.derived_location, '__typename'),
    }
    mutate({
      variables: ({
        form: formContents,
      } as unknown) as UpdateAccountHeaderMutationVariables,
    })
  }
  const isInvestor = account?.is_investor
  const isShareholder = account?.is_shareholder
  const initialValues: HeaderFormInputShape = merge(
    {
      id: account!.id,
      name: account!.name,
      type: account!.type,
      derived_location: {
        source: account!.derived_location.source,
      },
      client_type_id: account!.client_type_id,
      account_of_interest_reason: account!.account_of_interest_reason,
      account_of_interest_set_by_admin: account!.account_of_interest_set_by_admin,
      forge_declined_reason: account!.forge_declined_reason,
      forge_declined_set_by_admin: account!.forge_declined_set_by_admin,
    },
    {
      is_account_of_interest: account?.is_account_of_interest ?? false,
      is_forge_declined: account?.is_forge_declined ?? false,
      country: account?.derived_location?.countryCode,
      state: account?.derived_location?.state,
      city: account?.derived_location?.city,
      investorShareholder: [isShareholder && 'Shareholder', isInvestor && 'Investor'].filter(i => !!i) as Array<string>,
      mutationResult: mutateResult,
    }
  )

  return (
    <FormModal open hideModal={hideModal} title="Edit: Account">
      <Form
        render={props => <HeaderForm editSection={editSection} highlightDNCFields={highlightDNCFields} {...props} />}
        onSubmit={onSubmit}
        initialValues={initialValues}
      />
    </FormModal>
  )
}
